import React, { useCallback, useEffect, useMemo, useState } from 'react'
import T from 'prop-types'
import { useTranslation } from 'react-i18next'
import {
  Box,
  CircularProgress,
  Divider,
  IconButton,
  Typography,
  Button,
  Link,
} from '@mui/material'
import Collapse from '@mui/material/Collapse'
import MoreIcon from '@mui/icons-material/MoreHoriz'
import { TABLE_ROWS_PER_PAGE_OPTIONS } from 'lib/tableLib'
import { differenceInDays } from 'date-fns'
import { ROLES_SIXT, ROLE_PARTNER_ADMIN, useAuth } from 'auth'
// import getYearMonthDayDate from 'lib/getYearMonthDayDate'
import { useDebouncedCallback } from 'use-debounce'
import { Link as RLink } from 'react-router-dom'
import { COUNTRY_CONFIG_MAP } from 'lib/countryConfig'
import config from 'config'

import Table from 'components/atoms/Table'
import ConfirmBar from 'components/atoms/ConfirmBar'
import DownloadBar from 'components/atoms/DownloadBar'
import SearchField from 'components/atoms/SearchField'
import LogIcon from 'components/atoms/LogIcon'
import ClipboardWithTrendUp from 'components/atoms/ClipboardWithTrendUp'

import useTablePagination from 'hooks/useTablePagination'
import useEntityData from 'hooks/useEntityData'
import useCheckboxSelection from 'hooks/useCheckboxSelection'
import useConfirmationDialog from 'hooks/useConfirmationDialog'
import useLogs from 'hooks/useLogs'
import useReport from 'hooks/useReport'
import useExport from 'hooks/useExport'

import useInventory from './useInventory'
import usePartnerInventoryFilters from './usePartnerInventoryFilters'
import { useStateChange } from './useStateChange'
import { createColumns } from './Columns'
import { LeadsModal } from './Leads'
import useInventoryActions from './useInventoryActions'
import InventoryActionsMenu from './InventoryActionsMenu'
import {
  INVENTORY_ACTION_DOWNLOAD_DOCUMENTS,
  INVENTORY_ACTION_PRICE_CHANGES,
  INVENTORY_ACTION_STATE_HISTORY_INFO,
} from './stateChange/const'
import InventoryActionsDialog from './InventoryActionsDialog'
import useInventoryDocuments from './useInventoryDocuments'

// eslint-disable-next-line sonarjs/cognitive-complexity
const PartnerInventoryTable = ({ contentIsReadOnly = false }) => {
  const { t } = useTranslation()

  const countryConfig = COUNTRY_CONFIG_MAP[config.country ?? 'it']

  const { saveLogs, isFetching: isFetchingLogs } = useLogs()

  const { saveReport, isFetching: isFetchingReport } = useReport()

  const { saveExport, isFetching: isFetchingExport } = useExport()

  const { hasOneOfRoles } = useAuth()

  const partnerInventoryFilters = usePartnerInventoryFilters()

  const { filters, setSearchQuery, setSort, initFilters } =
    partnerInventoryFilters

  const [action, setAction] = useState()
  const [openLeadDialog, setOpenLeadDialog] = useState(false)
  const [anchorEl, setAnchorEl] = useState(null)

  const {
    setInLineError,
    getStateChangeAdditionalParameters,
    getStateChangeFormError,
    resetStateChangeForm,
    getStateChangeConfirmationFormFields,
  } = useStateChange()

  const [selectedCarForDocuments, setSelectedCarForDocuments] = React.useState()

  const {
    page: newPage,
    rowsPerPage: newRowsPerPage,
    handleChangePage: handleChangeTablePage,
    handleChangeRowsPerPage: handleChangeTableRowsPerPage,
  } = useTablePagination({ rowsPerPage: 100 })

  // eslint-disable-next-line sonarjs/cognitive-complexity
  const prepareFilters = useCallback((filters, filtersData) => {
    const daysNotFiltering =
      filters?.daysMin === filtersData?.daysMin &&
      filters?.daysMax === filtersData?.daysMax
    const priceNotFiltering =
      filters?.priceMin === filtersData?.priceMin &&
      filters?.priceMax === filtersData?.priceMax
    return {
      ...filters,
      brands: filters?.brands?.map((item) => {
        return item === 'all' ? 'all' : filtersData?.brands?.[item + 1].label
      }),
      models: filters?.models?.map((item) => {
        return item === 'all' ? 'all' : filtersData?.models?.[item + 1].label
      }),
      daysMin: daysNotFiltering ? 0 : filters?.daysMin,
      daysMax: daysNotFiltering ? 0 : filters?.daysMax,
      priceMin: priceNotFiltering ? 0 : filters?.priceMin,
      priceMax: priceNotFiltering ? 0 : filters?.priceMax,
    }
  }, [])

  const handlePrepareFilters = useCallback(() => {
    return prepareFilters(filters, partnerInventoryFilters.filtersData)
  }, [filters, partnerInventoryFilters.filtersData, prepareFilters])

  const {
    items,
    isFetching,
    total,
    others,
    isFetchingOtherPages,
    page,
    rowsPerPage,
    refetch: fetchData,
  } = useEntityData({
    newPage,
    limit: newRowsPerPage,
    filters: JSON.stringify(handlePrepareFilters()),
    sort: filters.order?.column,
    direction: filters.order?.direction,
  })

  const { isUpdating: isUpdatingAction, updateInventory } = useInventory()

  const {
    isFetching: isFetchingInventoryDocuments,
    documents,
    fetchInventoryDocuments,
    resetDocuments,
  } = useInventoryDocuments()

  const { selected, selectClick, isSelected, resetSelected } =
    useCheckboxSelection({ onlySingle: true })

  const { actionItemsEnabled } = useInventoryActions({ selected })

  const handleFilters = useDebouncedCallback(
    async () => fetchData('inventory/'),
    100
  )

  const numberFormatter = useMemo(() => new Intl.NumberFormat('it-IT'), [])

  useEffect(() => {
    if (isUpdatingAction) {
      return
    }
    handleFilters()
  }, [fetchData, newPage, newRowsPerPage, isUpdatingAction, handleFilters])

  useEffect(() => {
    if (!items.length || filters?.initialized) {
      return
    }
    initFilters(items)
  }, [items, initFilters, filters?.initialized])

  const sixtUser = useMemo(() => hasOneOfRoles(ROLES_SIXT), [hasOneOfRoles])
  const partnerAdmin = useMemo(
    () => hasOneOfRoles([ROLE_PARTNER_ADMIN]),
    [hasOneOfRoles]
  )

  const preparedItems = useMemo(
    () =>
      items.map((item) => {
        const price = item.lastPrice || item.price
        const fee = item.lastFee || item.fee
        return {
          ...item,
          price: item.negotiationSixtPrice || price,
          fee: item.negotiationSixtFee || fee,
          originalPrice:
            item.negotiationSixtPrice !== price ? price : undefined,
          originalFee: item.negotiationSixtFee !== fee ? fee : undefined,
          partner: <span id={`${item.partnerId}`}>{item.partnerName} </span>,
          daysToExpire: differenceInDays(new Date(item.expiresAt), new Date()),
          modelBestPrice: item.maxSell?.price,
          averagePrice: item.avgSell?.avgPrice,
          comments:
            others?.comments?.filter(
              (comment) => comment.inventoryId === item.inventoryId
            ) || [],
        }
      }),
    [items, others]
  )

  const readOnly = useMemo(
    () => isFetching || isFetchingOtherPages || isUpdatingAction,
    [isFetching, isFetchingOtherPages, isUpdatingAction]
  )

  const handleChangePage = (event, newPage) => {
    handleChangeTablePage(event, newPage)
    resetSelected()
  }

  const handleChangeRowsPerPage = (event) => {
    handleChangeTableRowsPerPage(event)
    resetSelected()
  }

  const handleRowClick = (row) => {
    selectClick(row.original, 'inventoryId')
  }

  const handleConfirmAction = async () => {
    if (
      [
        INVENTORY_ACTION_PRICE_CHANGES,
        INVENTORY_ACTION_STATE_HISTORY_INFO,
        INVENTORY_ACTION_DOWNLOAD_DOCUMENTS,
      ].includes(action)
    ) {
      return
    }

    // if (action === INVENTORY_ACTION_DOWNLOAD_REPORT) {
    //   formikRef.current.handleSubmit()
    //   return false
    // }

    const stateChangeError = getStateChangeFormError(action, selected)

    if (stateChangeError) {
      setInLineError(stateChangeError)
      return false
    }

    const additionalParams = await getStateChangeAdditionalParameters(action)

    await updateInventory(
      action,
      selected.map((car) => ({
        inventoryId: car.inventoryId,
        ...additionalParams,
      }))
    )
    await initFilters(items)
    //resetSelected()
  }

  // const handleDownloadReportSubmit = async (values) => {
  const handleDownloadReportSubmit = async () => {
    setFormLoading(true)
    await saveReport(
      'report_inventory',
      JSON.stringify(handlePrepareFilters())
      // getYearMonthDayDate(values.startDate),
      // getYearMonthDayDate(values.endDate)
    )
    setFormLoading(false)
    // handleCloseConfirmationDialog()
  }

  const {
    loading: confirmLoading,
    openConfirmationDialog,
    handleConfirmConfirmationDialog,
    handleOpenConfirmationDialog: handleOpenConfirmationDialogOriginal,
    handleCloseConfirmationDialog: handleCloseConfirmationDialogOriginal,
  } = useConfirmationDialog(handleConfirmAction)

  const handleOpenConfirmationDialog = useCallback(() => {
    resetStateChangeForm()
    handleOpenConfirmationDialogOriginal()
  }, [handleOpenConfirmationDialogOriginal, resetStateChangeForm])

  const handleOpenMenu = useCallback((event) => {
    setAnchorEl(event.currentTarget)
  }, [])

  const onOpenDocumentsToDownloadDialog = useCallback(
    (e, car) => {
      e.stopPropagation()
      fetchInventoryDocuments(car.inventoryId)
      setAction(INVENTORY_ACTION_DOWNLOAD_DOCUMENTS)
      handleOpenConfirmationDialog()
    },
    [fetchInventoryDocuments, handleOpenConfirmationDialog]
  )

  const onOpenPriceChangesDialog = useCallback(
    (e, car) => {
      e.stopPropagation()
      setSelectedCarForDocuments(car)
      setAction(INVENTORY_ACTION_PRICE_CHANGES)
      handleOpenConfirmationDialog()
    },
    [handleOpenConfirmationDialog]
  )

  const onOpenStateHistoryInfoDialog = useCallback(
    (e, car) => {
      e.stopPropagation()
      setSelectedCarForDocuments(car)
      setAction(INVENTORY_ACTION_STATE_HISTORY_INFO)
      handleOpenConfirmationDialog()
    },
    [handleOpenConfirmationDialog]
  )

  const columns = useMemo(
    () =>
      createColumns(
        items,
        sixtUser,
        isSelected,
        selected,
        readOnly,
        contentIsReadOnly,
        partnerInventoryFilters,
        numberFormatter,
        countryConfig,
        onOpenDocumentsToDownloadDialog,
        onOpenPriceChangesDialog,
        onOpenStateHistoryInfoDialog,
        t
      ),
    [
      items,
      sixtUser,
      isSelected,
      selected,
      readOnly,
      contentIsReadOnly,
      partnerInventoryFilters,
      numberFormatter,
      countryConfig,
      onOpenDocumentsToDownloadDialog,
      onOpenPriceChangesDialog,
      onOpenStateHistoryInfoDialog,
      t,
    ]
  )

  const handleDownload = async () => {
    const params = {
      page: 0,
      limit: total,
      filters: JSON.stringify(handlePrepareFilters()),
      sort: filters.order?.column,
      direction: filters.order?.direction,
    }
    await saveExport({ name: 'inventory', params })
  }

  const [formLoading, setFormLoading] = React.useState(false)

  return (
    <>
      <Table
        isFetching={isFetching}
        isFetchingOtherPages={isFetchingOtherPages}
        items={preparedItems}
        columns={columns}
        total={total}
        hasPagination={true}
        page={page}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={TABLE_ROWS_PER_PAGE_OPTIONS}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        headerProps={{ sx: { px: 1 } }}
        cellProps={{ sx: { px: 1 } }}
        getRowProps={(row) => {
          const noAction = contentIsReadOnly
          return {
            onClick: (e) => {
              !noAction && handleRowClick(row)
            },
            sx: { cursor: !noAction ? 'pointer' : undefined },
          }
        }}
        sortable
        excludedSortableIds={[
          'checkbox',
          'options',
          'mandatoryToBuy',
          'comments',
          'linkAppraisal',
          'documentsToDownload',
        ]}
        orderColumn={filters?.order?.column}
        orderDirection={filters?.order?.direction}
        handleSort={setSort}
        stickyHeader
        bodyMaxHeight={`calc(100vh - ${80 + 20 + 32 + 73 + 20 + 60 + 20}px)`} //mainPaddingTop + pagePaddingTop + title + titleMarginBottom + tableHeader + tableMarginBottom + pagePaddingBottom
        tablePaginationProps={{ className: 'content-container wide' }}
        top={
          <>
            <ConfirmBar
              component="div"
              hideConfirm
              left={
                <>
                  <SearchField
                    data-testid="text-search"
                    placeholder={t('common.form.searchBy')}
                    value={filters.query}
                    onChange={(event) => setSearchQuery(event.target.value)}
                  />
                </>
              }
              leftProps={{ sx: { width: 0.5 } }}
              right={
                <>
                  <Box pr={2} />
                  <Typography
                    variant="body2"
                    color="textPrimary"
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    {t('partnerInventory.filteredStock')}
                  </Typography>
                  <Box pr={1} />
                  <Typography
                    variant="body2"
                    color="textPrimary"
                    sx={{ whiteSpace: 'nowrap' }}
                  >
                    <strong>
                      {numberFormatter.format(others?.filteredStock || 0)}
                    </strong>
                  </Typography>
                  {sixtUser && (
                    <>
                      <Box pr={2} />
                      <Typography
                        variant="body2"
                        color="textPrimary"
                        sx={{ whiteSpace: 'nowrap' }}
                      >
                        {t('partnerInventory.filteredValue')}{' '}
                      </Typography>
                      <Box pr={1} />
                      <Typography
                        variant="body2"
                        color="textPrimary"
                        sx={{ whiteSpace: 'nowrap' }}
                      >
                        <strong>
                          {`€ ${numberFormatter.format(
                            others?.filteredStock && others?.filteredValue
                              ? Math.round(
                                  (others.filteredValue /
                                    others.filteredStock) *
                                    100
                                ) / 100
                              : 0
                          )}`}
                        </strong>
                      </Typography>
                    </>
                  )}
                  <Box pr={2} />
                  <>
                    <Collapse orientation="horizontal" in={isFetchingReport}>
                      <div style={{ marginLeft: 4, marginRight: 4 }}>
                        <CircularProgress size={20} />
                      </div>
                    </Collapse>
                    <ClipboardWithTrendUp
                      disabled={isFetchingReport}
                      handleClick={() => {
                        handleDownloadReportSubmit()
                        // setAction(INVENTORY_ACTION_DOWNLOAD_REPORT)

                        // Set the start date from a year ago
                        // const now = new Date()
                        // setInitialDownloadReportValues({
                        //   startDate: addYears(now, -1),
                        //   endDate: now,
                        // })
                        // handleOpenConfirmationDialog()
                      }}
                    />
                    <Collapse orientation="horizontal" in={isFetchingLogs}>
                      <div style={{ marginLeft: 4, marginRight: 4 }}>
                        <CircularProgress size={20} />
                      </div>
                    </Collapse>
                    <LogIcon
                      disabled={isFetchingLogs}
                      handleClick={() =>
                        saveLogs(`logs_partner_inventory`, {
                          area: t('common.logs.partner-inventory'),
                        })
                      }
                    />
                  </>
                  <Collapse orientation="horizontal" in={isFetchingExport}>
                    <div style={{ marginLeft: 4, marginRight: 4 }}>
                      <CircularProgress size={20} />
                    </div>
                  </Collapse>
                  <DownloadBar
                    readOnly={readOnly}
                    handleDownload={handleDownload}
                    disabled={isFetchingExport}
                  />
                  {sixtUser && (
                    <Link component={RLink} to="/inventory-reprice">
                      <Button variant="text">
                        {t('partnerInventory.goToReprice')}
                      </Button>
                    </Link>
                  )}
                  {partnerAdmin && (
                    <Button
                      variant="text"
                      onClick={() => {
                        setOpenLeadDialog(true)
                      }}
                      data-testid={'open-lead-dialog-button'}
                    >
                      {t('partnerInventory.lead.openButton')}
                    </Button>
                  )}
                  <IconButton
                    data-testid="options-action"
                    aria-label="More"
                    onClick={handleOpenMenu}
                    disabled={
                      !selected.length ||
                      readOnly ||
                      !Object.values(actionItemsEnabled).filter((i) => i).length
                    }
                  >
                    <MoreIcon />
                  </IconButton>
                </>
              }
            />
            <Divider />
          </>
        }
      />
      <InventoryActionsMenu
        anchorEl={anchorEl}
        setAnchorEl={setAnchorEl}
        setAction={setAction}
        handleOpenConfirmationDialog={handleOpenConfirmationDialog}
        actionItemsEnabled={actionItemsEnabled}
      />
      {openLeadDialog && (
        <LeadsModal
          onRefresh={handleFilters}
          onClose={() => setOpenLeadDialog(false)}
        />
      )}
      {/**  Edit here */}
      {openConfirmationDialog && (
        <InventoryActionsDialog
          selected={selected}
          action={action}
          handleCloseConfirmationDialogOriginal={() => {
            resetDocuments()
            handleCloseConfirmationDialogOriginal()
          }}
          isFetching={isFetchingInventoryDocuments}
          documents={documents}
          selectedCarForDocuments={selectedCarForDocuments}
          setSelectedCarForDocuments={setSelectedCarForDocuments}
          handleConfirmConfirmationDialog={handleConfirmConfirmationDialog}
          getStateChangeConfirmationFormFields={
            getStateChangeConfirmationFormFields
          }
          handlePrepareFilters={handlePrepareFilters}
          setFormLoading={setFormLoading}
          formLoading={formLoading}
          confirmLoading={confirmLoading}
          openConfirmationDialog={openConfirmationDialog}
          readOnly={readOnly}
        />
      )}
    </>
  )
}

PartnerInventoryTable.propTypes = {
  contentIsReadOnly: T.bool,
}

export default PartnerInventoryTable
